import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Loading } from "@/components/common";

import Login from "@/pages/Login";
import PatientDashboard from "@/pages/patient/PatientDashboard";
import ExerciseDetail from "@/pages/patient/ExerciseDetail";
import Diary from "@/pages/patient/Diary";
import BraceLogs from "@/pages/patient/BraceLogs";
import Education from "@/pages/patient/Education";
import Chat from "@/pages/patient/Chat";
import Profile from "@/pages/Profile";

import PhysioDashboard from "@/pages/physio/PhysioDashboard";
import Patients from "@/pages/physio/Patients";
import PatientDetail from "@/pages/physio/PatientDetail";
import ExercisesArchive from "@/pages/physio/ExercisesArchive";
import Articles from "@/pages/physio/Articles";
import AssistanceMinutes from "@/pages/physio/AssistanceMinutes";
import PhysioChat from "@/pages/physio/Chat";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminExercises from "@/pages/admin/AdminExercises";
import AdminAssistance from "@/pages/admin/AdminAssistance";
import PendingContent from "@/pages/admin/PendingContent";

const homeFor = (role) =>
  role === "admin" ? "/admin" : role === "fisioterapista" ? "/physio" : "/patient";

const Protected = ({ roles, children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="s2m-gradient min-h-screen"><Loading /></div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return <Layout>{children}</Layout>;
};

const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="s2m-gradient min-h-screen"><Loading /></div>;
  return <Navigate to={user ? homeFor(user.role) : "/login"} replace />;
};

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />

            <Route path="/patient" element={<Protected roles={["paziente"]}><PatientDashboard /></Protected>} />
            <Route path="/patient/exercise/:id" element={<Protected roles={["paziente"]}><ExerciseDetail /></Protected>} />
            <Route path="/patient/diary" element={<Protected roles={["paziente"]}><Diary /></Protected>} />
            <Route path="/patient/brace" element={<Protected roles={["paziente"]}><BraceLogs /></Protected>} />
            <Route path="/patient/education" element={<Protected roles={["paziente"]}><Education /></Protected>} />
            <Route path="/patient/chat" element={<Protected roles={["paziente"]}><Chat /></Protected>} />

            <Route path="/physio" element={<Protected roles={["fisioterapista"]}><PhysioDashboard /></Protected>} />
            <Route path="/physio/patients" element={<Protected roles={["fisioterapista"]}><Patients /></Protected>} />
            <Route path="/physio/patients/:id" element={<Protected roles={["fisioterapista"]}><PatientDetail /></Protected>} />
            <Route path="/physio/exercises" element={<Protected roles={["fisioterapista"]}><ExercisesArchive /></Protected>} />
            <Route path="/physio/articles" element={<Protected roles={["fisioterapista"]}><Articles /></Protected>} />
            <Route path="/physio/assistance" element={<Protected roles={["fisioterapista"]}><AssistanceMinutes /></Protected>} />
            <Route path="/physio/chat" element={<Protected roles={["fisioterapista"]}><PhysioChat /></Protected>} />

            <Route path="/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
            <Route path="/admin/users" element={<Protected roles={["admin"]}><AdminUsers /></Protected>} />
            <Route path="/admin/exercises" element={<Protected roles={["admin"]}><AdminExercises /></Protected>} />
            <Route path="/admin/articles" element={<Protected roles={["admin"]}><Articles /></Protected>} />
            <Route path="/admin/assistance" element={<Protected roles={["admin"]}><AdminAssistance /></Protected>} />
            <Route path="/admin/content" element={<Protected roles={["admin"]}><PendingContent /></Protected>} />

            <Route path="*" element={<RootRedirect />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
